// src/components/NewVehicleFeaturesTab.tsx

import React from 'react';
import { Box, Typography, Paper, Grid, Stack } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Category Icons
import SpeedIcon from '@mui/icons-material/Speed';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import SpeakerOutlinedIcon from '@mui/icons-material/SpeakerOutlined';

import { VehicleVariant, Feature } from '../types/NewVehicleTypes';

interface NewVehicleFeaturesTabProps {
  variant?: VehicleVariant;
}

// Helper to map category names to specific icons
const getCategoryIcon = (groupName: string) => {
  const name = groupName.toLowerCase();
  if (name.includes('performance')) return <SpeedIcon fontSize="small" />;
  if (name.includes('safety')) return <ShieldOutlinedIcon fontSize="small" />;
  if (name.includes('exterior')) return <DirectionsCarOutlinedIcon fontSize="small" />;
  if (name.includes('comfort') || name.includes('interior')) return <AirlineSeatReclineNormalIcon fontSize="small" />;
  if (name.includes('convenience')) return <WidgetsOutlinedIcon fontSize="small" />;
  if (name.includes('infotainment')) return <SpeakerOutlinedIcon fontSize="small" />;
  if (name.includes('light')) return <LightbulbOutlinedIcon fontSize="small" />;
  return <SettingsSuggestOutlinedIcon fontSize="small" />; // Default icon
};

// Helper component for a single feature row (Matches SpecRow styling)
const FeatureRow = ({ featureName }: { featureName: string }) => {
  return (
    <Box 
      display="flex" 
      alignItems="center"
      py={1.5} 
      sx={{ 
        borderBottom: '1px solid #f3f4f6',
        '&:last-child': { borderBottom: 'none' } 
      }}
    >
      <CheckCircleOutlineIcon 
        sx={{ 
          color: '#2e7d32', 
          fontSize: 18, 
          mr: 1.5 
        }} 
      />
      <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ flex: 1 }}>
        {featureName}
      </Typography>
    </Box>
  );
};

const NewVehicleFeaturesTab: React.FC<NewVehicleFeaturesTabProps> = ({ variant }) => {
  const features = variant?.featureGroup?.features || [];

  // Group the flat features array by the 'featureGroup' property
  const groupedFeatures = features.reduce((acc, feature) => {
    const groupName = feature.featureGroup || 'Others';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(feature);
    return acc;
  }, {} as Record<string, Feature[]>);

  const groupEntries = Object.entries(groupedFeatures);

  if (!variant || groupEntries.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', mt: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No features available for this variant.
        </Typography>
      </Box>
    );
  }

  // Split categories evenly into left and right columns for masonry-like display
  const leftColumnGroups: [string, Feature[]][] = [];
  const rightColumnGroups: [string, Feature[]][] = [];

  groupEntries.forEach((entry, index) => {
    if (index % 2 === 0) {
      leftColumnGroups.push(entry);
    } else {
      rightColumnGroups.push(entry);
    }
  });

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        
        {/* LEFT COLUMN */}
        <Grid size={{ xs: 12, md: 6 }}>
          {leftColumnGroups.map(([groupName, groupFeatures], idx) => (
            <Paper key={idx} variant="outlined" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Box sx={{ display: 'flex', color: 'text.primary' }}>
                  {getCategoryIcon(groupName)}
                </Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {groupName}
                </Typography>
              </Stack>
              <Box>
                {groupFeatures.map((feature) => (
                  <FeatureRow key={feature.featureId} featureName={feature.featureName} />
                ))}
              </Box>
            </Paper>
          ))}
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid size={{ xs: 12, md: 6 }}>
          {rightColumnGroups.map(([groupName, groupFeatures], idx) => (
            <Paper key={idx} variant="outlined" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Box sx={{ display: 'flex', color: 'text.primary' }}>
                  {getCategoryIcon(groupName)}
                </Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {groupName}
                </Typography>
              </Stack>
              <Box>
                {groupFeatures.map((feature) => (
                  <FeatureRow key={feature.featureId} featureName={feature.featureName} />
                ))}
              </Box>
            </Paper>
          ))}
        </Grid>

      </Grid>
    </Box>
  );
};

export default NewVehicleFeaturesTab;